import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface GoogleFont {
  family: string;
  variants: string[];
  category: string;
}

@Injectable({
  providedIn: 'root'
})
export class GoogleFontsService {
  private loadedFonts: Set<string> = new Set();
  private fontsSubject = new BehaviorSubject<GoogleFont[]>([
    { family: 'Roboto', variants: ['400', '500', '700'], category: 'sans-serif' },
    { family: 'Open Sans', variants: ['400', '500', '700'], category: 'sans-serif' },
    { family: 'Lato', variants: ['400', '500', '700'], category: 'sans-serif' },
    { family: 'Montserrat', variants: ['400', '500', '700'], category: 'sans-serif' },
    { family: 'Poppins', variants: ['400', '500', '700'], category: 'sans-serif' },
    { family: 'Source Sans Pro', variants: ['400', '500', '700'], category: 'sans-serif' },
    { family: 'Roboto Condensed', variants: ['400', '500', '700'], category: 'sans-serif' },
    { family: 'Oswald', variants: ['400', '500', '700'], category: 'sans-serif' },
    { family: 'Raleway', variants: ['400', '500', '700'], category: 'sans-serif' },
    { family: 'Ubuntu', variants: ['400', '500', '700'], category: 'sans-serif' },
    { family: 'Merriweather', variants: ['400', '700'], category: 'serif' },
    { family: 'Playfair Display', variants: ['400', '500', '700'], category: 'serif' },
    { family: 'Lora', variants: ['400', '500', '700'], category: 'serif' },
    { family: 'PT Serif', variants: ['400', '700'], category: 'serif' },
    { family: 'Roboto Mono', variants: ['400', '500', '700'], category: 'monospace' },
    { family: 'Source Code Pro', variants: ['400', '500', '700'], category: 'monospace' },
    { family: 'Fira Code', variants: ['400', '500', '700'], category: 'monospace' },
    { family: 'JetBrains Mono', variants: ['400', '500', '700'], category: 'monospace' }
  ]);

  public fonts$ = this.fontsSubject.asObservable();

  constructor() {}

  public loadFontToPage(fontFamily: string) {
    if (this.loadedFonts.has(fontFamily)) {
      return;
    }

    // Skip loading for system fonts
    const systemFonts = ['Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Courier New', 'Verdana'];
    if (systemFonts.includes(fontFamily)) {
      return;
    }

    const link = document.createElement('link');
    // Convert font family name to URL-friendly format
    const encodedFamily = fontFamily.replace(/\s+/g, '+');
    link.href = `https://fonts.googleapis.com/css2?family=${encodedFamily}:wght@400;500;700&display=swap`;
    link.rel = 'stylesheet';
    
    link.onload = () => {
      this.loadedFonts.add(fontFamily);
      console.log(`Font ${fontFamily} loaded successfully`);
    };

    link.onerror = () => {
      console.error(`Error loading font ${fontFamily}`);
    };

    document.head.appendChild(link);
  }

  public getLoadedFonts(): string[] {
    return Array.from(this.loadedFonts);
  }

  public formatFontFamily(fontFamily: string): string {
    // Skip formatting for system fonts
    const systemFonts = ['Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Courier New', 'Verdana'];
    if (systemFonts.includes(fontFamily)) {
      return `${fontFamily}, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`;
    }

    return `"${fontFamily}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`;
  }
}